import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { SelectField, TextField } from "@/components/Field";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { call, CALLABLES } from "@/services/callables";
import { toAppError } from "@/services/errors";
import type { Locale } from "@/types";
import { LOCALE_OPTIONS } from "@/i18n";

/**
 * Adding one person by hand.
 *
 * **There is no email field in the player flow.** Not disabled, not optional —
 * absent. No player email address is collected anywhere in Spontar, and a field
 * that could be filled in is a field that eventually is.
 *
 * A player gets no password either: they hold a device the institution has
 * registered and never sign in. A teacher is invited: the backend creates the
 * account and emails a sign-in link, so for a teacher the address is the one
 * thing this form cannot do without.
 */
export function NewPersonPage() {
  const t = useT();
  const { locale: uiLocale } = useI18n();
  const navigate = useNavigate();
  const { success, showError, info } = useToast();

  const [role, setRole] = useState<"teacher" | "student">("student");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const { institution } = useAuth();
  // A new person starts in the institution's language — a French institution
  // adds French-speaking people — and falls back to the language on screen.
  const [locale, setLocale] = useState<Locale>(institution?.locale ?? uiLocale);
  const [busy, setBusy] = useState(false);
  const [seatLimit, setSeatLimit] = useState<Record<string, unknown> | undefined>(undefined);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const issued = await call<
        { displayName: string; locale: Locale; email?: string },
        {
          uid: string;
          displayName: string;
          username?: string;
          email?: string;
          /** A teacher: whether the sign-in link reached their address. */
          emailSent?: boolean;
          /** Legacy: a printed password, when the backend still issues one. */
          temporaryPassword?: string;
        }
      >(role === "teacher" ? CALLABLES.createTeacher : CALLABLES.createPlayer, {
        displayName: displayName.trim(),
        locale,
        // Only ever sent for a teacher — a player has no address, by design.
        ...(role === "teacher" ? { email: email.trim() } : {}),
      });

      success("newPerson.created", { name: displayName.trim() });

      if (issued.temporaryPassword) {
        // The password exists here and in the Auth hash, and nowhere else.
        info("person.passwordIssued", {
          name: issued.displayName,
          password: issued.temporaryPassword,
        });
        info("person.passwordIssuedWarning");
      } else if (role === "teacher") {
        // Invited: the link went by email, or it did not, and the owner is
        // told which so the teacher is not left waiting for a mail that
        // never comes.
        if (issued.emailSent) info("newPerson.invited", { email: email.trim() });
        else info("newPerson.inviteNotSent", { name: issued.displayName });
      } else {
        // A player. Nothing was issued, so nothing is shown — and the next step
        // is a device, which is where the sentence points.
        info("person.noCredentials", { name: issued.displayName });
      }
      navigate(`/people/${issued.uid}`);
    } catch (error) {
      const appError = toAppError(error);
      /*
        A seat limit is not a dead end. An owner who has run out of seats needs
        the numbers and a way forward on the same screen, not an error and a
        hunt through the navigation for where licences live.
      */
      if (appError.key === "billing.error.seatLimit") setSeatLimit(appError.params);
      else showError(appError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionShell titleKey="newPerson.title">
      <form className="flex max-w-lg flex-col gap-4" onSubmit={onSubmit}>
        <SelectField
          labelKey="newPerson.role"
          value={role}
          onChange={(event) => setRole(event.target.value as "teacher" | "student")}
          options={[
            { value: "student", labelKey: "newPerson.role.student" },
            { value: "teacher", labelKey: "newPerson.role.teacher" },
          ]}
        />

        <TextField
          labelKey="newPerson.name"
          required
          autoFocus
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />

        {role === "teacher" ? (
          <TextField
            labelKey="newPerson.email"
            hintKey="newPerson.emailHint"
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        ) : (
          /*
            Not a disabled field. There is nothing here to fill in, and the
            paragraph says why rather than leaving an owner to wonder whether
            they have missed something.
          */
          <p className="surface-inset px-4 py-3 text-sm text-ink-soft">
            {t("newPerson.noEmailForStudents")}
          </p>
        )}

        <SelectField
          labelKey="newPerson.locale"
          value={locale}
          onChange={(event) => setLocale(event.target.value as Locale)}
          options={LOCALE_OPTIONS}
        />

        {seatLimit ? (
          <div className="rounded-card border border-warning bg-warning-soft px-4 py-3">
            <p className="text-sm text-ink">{t("billing.seatLimitInline", seatLimit)}</p>
            <Link
              to="/billing"
              className="mt-2 inline-block text-sm font-medium text-ink underline underline-offset-2"
            >
              {t("billing.seatLimitAction")}
            </Link>
          </div>
        ) : null}

        <div>
          <Button
            type="submit"
            busy={busy}
            disabled={displayName.trim().length === 0 || (role === "teacher" && email.trim().length === 0)}
          >
            {t("newPerson.create")}
          </Button>
        </div>
      </form>
    </SectionShell>
  );
}
